import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';

interface TechnicalSEOEnhancerProps {
  canonicalUrl: string;
  alternateUrls?: Array<{ href: string; hreflang: string }>;
  robotsContent?: string;
  structuredData?: object[];
}

/**
 * Technical SEO Enhancer Component
 * Implements advanced technical SEO optimizations
 */
const TechnicalSEOEnhancer: React.FC<TechnicalSEOEnhancerProps> = ({
  canonicalUrl,
  alternateUrls = [],
  robotsContent = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  structuredData = []
}) => {
  useEffect(() => {
    // Implement advanced performance monitoring
    const initializePerformanceTracking = () => {
      if ('performance' in window && 'PerformanceObserver' in window) {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.entryType === 'largest-contentful-paint') {
              console.log('LCP:', entry.startTime);
            }
            if (entry.entryType === 'first-input') {
              const fidEntry = entry as PerformanceEventTiming;
              console.log('FID:', fidEntry.processingStart - fidEntry.startTime);
            }
          }
        });

        try {
          observer.observe({ entryTypes: ['largest-contentful-paint', 'first-input'] });
        } catch (e) {
          // Fallback for older browsers
        }
      }
    };

    const timer = setTimeout(() => {
      initializePerformanceTracking();
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Generate breadcrumb structured data based on current URL
  const generateBreadcrumbSchema = () => {
    const pathSegments = canonicalUrl.replace(/^https?:\/\/[^\/]+/, '').split('/').filter(Boolean);
    
    if (pathSegments.length === 0) return null;

    const breadcrumbList = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": canonicalUrl.split('/').slice(0, 3).join('/')
        },
        ...pathSegments.map((segment, index) => ({
          "@type": "ListItem",
          "position": index + 2,
          "name": segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' '),
          "item": canonicalUrl.split('/').slice(0, 4 + index).join('/')
        }))
      ]
    };

    return breadcrumbList;
  };

  const breadcrumbSchema = generateBreadcrumbSchema();

  return (
    <Helmet>
      {/* Enhanced robots meta */}
      <meta name="googlebot" content={robotsContent} />
      <meta name="bingbot" content={robotsContent} />

      {/* Alternate language URLs */}
      {alternateUrls.map(alt => (
        <link key={alt.hreflang} rel="alternate" hrefLang={alt.hreflang} href={alt.href} />
      ))}

      {/* Technical SEO meta tags */}
      <meta name="referrer" content="no-referrer-when-downgrade" />
      <meta httpEquiv="x-dns-prefetch-control" content="on" />
      
      {/* Client Hints */}
      <meta httpEquiv="Accept-CH" content="DPR, Width, Viewport-Width" />

      {/* Breadcrumb structured data */}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema, null, 2)}
        </script>
      )}

      {/* Additional structured data */}
      {structuredData.map((schema, index) => (
        <script key={`schema-${index}`} type="application/ld+json">
          {JSON.stringify(schema, null, 2)}
        </script>
      ))}

      {/* No external dns-prefetch - all resources are self-hosted for DSGVO compliance */}
    </Helmet>
  );
};

export default TechnicalSEOEnhancer;
